import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { getHomeownerToken } from '../utils/storage';
import { homeownerRecords } from '../utils/homeownerApi';
import { classifyStoredPhoto } from './storagePaths';

const SIGNED_SECONDS = 60 * 60;

/**
 * A signed read URL for a private object. External addresses are returned
 * unchanged. Property photos for a homeowner session are signed by
 * homeowner-records. Agent sessions sign with their own storage access.
 */
export async function signStorageRead(bucket, stored) {
  const located = classifyStoredPhoto(stored, bucket);
  if (located.kind === 'empty') return '';
  if (located.kind === 'external' || located.kind === 'signed') return located.src;
  if (!located.path) return '';

  const auth = supabase && supabase.auth;
  if (auth && typeof auth.getSession === 'function' && supabase.storage && typeof supabase.storage.from === 'function') {
    try {
      const sessionResult = await auth.getSession();
      const session = sessionResult && sessionResult.data && sessionResult.data.session;
      if (session) {
        const signed = await supabase.storage.from(bucket).createSignedUrl(located.path, SIGNED_SECONDS);
        if (signed && !signed.error && signed.data && signed.data.signedUrl) return signed.data.signedUrl;
      }
    } catch (error) {
      // The homeowner path below covers a session that cannot sign.
    }
  }

  if (bucket === 'property-photos' && getHomeownerToken()) {
    try {
      const result = await homeownerRecords({
        resource: 'property_photo',
        action: 'signed_url',
        path: located.path,
      });
      if (result && result.url) return result.url;
    } catch (error) {
      return '';
    }
  }
  return '';
}

export function useSignedStorageUrl(bucket, stored) {
  const located = classifyStoredPhoto(stored, bucket);
  const immediate = located.kind === 'external' || located.kind === 'signed' ? located.src : '';
  const [src, setSrc] = useState(immediate);

  useEffect(() => {
    let cancel = false;
    if (located.kind === 'empty') {
      setSrc('');
      return undefined;
    }
    if (located.kind === 'external' || located.kind === 'signed') {
      setSrc(located.src);
      return undefined;
    }
    setSrc('');
    signStorageRead(bucket, stored).then((url) => {
      if (!cancel) setSrc(url || '');
    });
    return () => {
      cancel = true;
    };
  }, [bucket, stored, located.kind, located.src, located.path]);

  return src;
}
